import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AlertCircle, AlertTriangle, ArrowLeft, Check, ChevronDown, ChevronRight, Info, Search, X } from 'lucide-react';

import { SoftCard } from '@/components/ui/soft-card';
import { Skeleton } from '@/components/ui/skeleton';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { useToast } from '@/components/ui/toast';
import { useShop } from '@/hooks/useShop';
import { useGoBack } from '@/hooks/useGoBack';
import { useInventoryPageSummary } from '@/hooks/useInventory';
import { getSearchCollection } from '@services/stockSearch';
import myShopClient from '@services/nearlive';
import { download } from '@utils/download';
import { cn } from '@/lib/utils';

import {
	HideProductsDialog,
	UnhideProductsDialog,
	HideWithoutTitleDialog,
	UploadProductDataDialog
} from './components/ProductActionDialogs';
import { computeVisibilityCounts } from './lib/channel-visibility';

const PAGE_SIZE = 50;

// Issue types — these mirror the Inventory landing page's profile-completeness
// attributes. `label` is the filter-popover checkbox; `issue` is the Typesense
// `issues` value; `title`/`sub` re-skin the page heading when a single issue is
// the only filter (e.g. arriving via a Fix CTA's ?issue= deep-link).
const ISSUE_CONFIG = {
	'missing-title': {
		issue: 'MissingProductTitle',
		label: 'Missing title',
		title: 'Products missing a title',
		sub: 'These barcode-only products are hidden from shoppers until you add a title.',
		banner: 'Showing only products with no title'
	},
	'missing-image': {
		issue: 'MissingProductImages',
		label: 'Missing image',
		title: 'Products missing images',
		sub: 'Add images to unlock Local Storefront, Meta and other visual channels.',
		banner: 'Showing only products missing images'
	},
	'missing-description': {
		issue: 'MissingProductDescription',
		label: 'Missing description',
		title: 'Products missing a description',
		sub: 'Add descriptions so shoppers know what they are buying.',
		banner: 'Showing only products missing a description'
	},
	'missing-brand': {
		issue: 'MissingProductBrand',
		label: 'Missing brand',
		title: 'Products missing a brand',
		sub: 'Add a brand to help local shoppers find these in search and filters.',
		banner: 'Showing only products missing a brand'
	},
	'missing-category': {
		issue: 'MissingProductCategory',
		label: 'Missing storefront category',
		title: 'Products missing a storefront category',
		sub: 'Add a storefront category to help local shoppers find these in search and filters.',
		banner: 'Showing only products missing a storefront category'
	}
};

const CHANNEL_LABELS = {
	google: 'Google Local Listings',
	'local-ads': 'Google Local Inventory Ads',
	'local-checkout': 'Local Checkout',
	'product-reservations': 'Product Reservations',
	storefront: 'Local Storefront',
	deliveroo: 'Deliveroo',
	'uber-eats': 'Uber Eats',
	'meta-commerce': 'Meta',
	'tiktok-shop-feed': 'TikTok',
	'pinterest-feed': 'Pinterest',
	'snap-store-feed': 'Snap'
};
const channelLabel = (id) => CHANNEL_LABELS[id] ?? id.replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

// Backtick-quote a Typesense filter value so categories with spaces / `&` parse.
const tsValue = (v) => `\`${String(v).replace(/`/g, '')}\``;

// Precedence: has_errors still wins over Hidden (a retailer may have hidden a
// product BECAUSE of its data issues — the data signal must still surface, per
// NEA-6065; a hidden row computes 0 visible / 0 blocked so it falls to
// 'inactive' below, never 'hidden'). For rows with errors the pill follows the
// per-channel counts: a mix of blocked and visible renders the split 'partial'
// pill (NEA-6106); nothing blocked but ≥1 channel visible renders 'Active' —
// on a Google + Storefront shop, issues like a missing category block neither
// channel (Google self-sources, Storefront only needs a title), so the flat
// 'Inactive' pill was calling fully-live products inactive while GMC and the
// storefront showed them (NEA-6222; the Issues column still surfaces the gap).
// 'Inactive' remains for rows visible nowhere.
//
// The Active/Inactive/Hidden filter still writes the original Typesense
// clauses (see `filterParts` below) — the filter → pill invariant is
// deliberately loose here: Partial and Active-with-issues rows currently
// surface under the "Inactive" filter (because Typesense's has_errors is true)
// until the follow-up ticket adds a `visible_channel_count_i` field so we can
// filter on actual visibility.
function computeProductStatus(record, visibility) {
	if (record.has_errors) {
		if (visibility?.blocked > 0) return 'partial';
		if (visibility?.visible > 0) return 'active';
		return 'inactive';
	}
	if (record.enabled_i === false) return 'hidden';
	return 'active';
}

function StatusPill({ status }) {
	if (status === 'inactive') {
		return (
			<span className="inline-flex items-center rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-semibold text-amber-700">
				Inactive
			</span>
		);
	}
	if (status === 'hidden') {
		return (
			<span className="inline-flex items-center rounded-full bg-muted px-2 py-0.5 text-[11px] font-semibold text-muted-foreground">
				Hidden
			</span>
		);
	}
	return (
		<span className="inline-flex items-center rounded-full bg-success-soft px-2 py-0.5 text-[11px] font-semibold text-success-soft-foreground">
			Active
		</span>
	);
}

// Split "some visible / some blocked" pill for products whose channel coverage
// is mixed. `visible` is the count of enabled channels the product is currently
// live on; `blocked` is the count of enabled channels the product is on but
// gated by a data issue (missing title/image/etc.). Retailer-hidden channels
// are excluded from both — they're a deliberate action, not a data problem.
function VisibilityPill({ visible, blocked }) {
	return (
		<span
			className="inline-flex items-center rounded-full text-[11px] font-semibold leading-none"
			aria-label={`${visible} visible, ${blocked} blocked`}
		>
			<span className="inline-flex items-center gap-1 rounded-l-full bg-success-soft px-2 py-1 text-success-soft-foreground">
				<Check className="h-3 w-3" aria-hidden="true" />
				<span className="tabular-nums">{visible}</span>
			</span>
			<span className="inline-flex items-center gap-1 rounded-r-full bg-amber-100 px-2 py-1 text-amber-700">
				<AlertTriangle className="h-3 w-3" aria-hidden="true" />
				<span className="tabular-nums">{blocked}</span>
			</span>
		</span>
	);
}

// Status and Issues are independent verdicts and retailers read them as one
// (NEA-6107): a product can carry an issue and still be Active, because the
// issue blocks no channel this shop has enabled. Say so in the header rather
// than leaving the two columns to look like they contradict each other.
const COLUMN_HINTS = {
	status: "Where this product stands across the channels you've enabled. Active means it's showing on at least one of them; a split count means it's live on some and held back on others; Hidden means you've hidden it yourself.",
	issues: "Gaps in this product's data — a missing title, image, brand, description or category. A gap doesn't always hide a product: open the product to see which channels it holds back, and fix it there."
};

function ColumnHint({ label, hint }) {
	return (
		<span className="inline-flex items-center gap-1">
			{label}
			<TooltipProvider delayDuration={150}>
				<Tooltip>
					<TooltipTrigger asChild>
						<button
							type="button"
							aria-label={`About the ${label} column`}
							className="inline-flex h-4 w-4 items-center justify-center rounded-full text-muted-foreground/70 transition hover:text-foreground"
						>
							<Info className="h-3 w-3" />
						</button>
					</TooltipTrigger>
					<TooltipContent side="top" className="max-w-xs text-left text-xs normal-case leading-relaxed tracking-normal">
						{hint}
					</TooltipContent>
				</Tooltip>
			</TooltipProvider>
		</span>
	);
}

const EMPTY_FILTERS = { availability: null, status: null, edited: false, channels: [], categories: [], issues: [] };

// Filters live in the URL so they survive the unmount/back round-trip when a
// user opens a product and returns (NEA-5928). Two URL shapes are accepted:
//
//   - Fix-CTA shorthand: `?issue=missing-brand` (no `f` marker). The profile-gap
//     counts behind those CTAs are over in-stock items only (inventory-page-
//     summary), so a shorthand arrival pre-selects In stock to match (NEA-5916).
//   - Canonical: `?f=1&…` written by this page whenever it owns the filters.
//     Here In stock is honoured verbatim — its absence means the user cleared it,
//     so we must NOT re-default it (that's the back-nav bug this distinguishes).
//
// Array filters use repeated keys so category names with commas / `&` survive.
const parseFilters = (searchParams) => {
	const issues = searchParams.getAll('issue').filter((k) => ISSUE_CONFIG[k]);
	if (!searchParams.has('f')) {
		return { ...EMPTY_FILTERS, issues, availability: issues.length ? 'in' : null };
	}
	return {
		availability: searchParams.get('availability') || null,
		status: searchParams.get('status') || null,
		edited: searchParams.get('edited') === '1',
		channels: searchParams.getAll('channel'),
		categories: searchParams.getAll('category'),
		issues
	};
};

const serializeFilters = (filters) => {
	const params = new URLSearchParams();
	filters.issues.forEach((k) => params.append('issue', k));
	if (filters.availability) params.set('availability', filters.availability);
	if (filters.status) params.set('status', filters.status);
	if (filters.edited) params.set('edited', '1');
	filters.channels.forEach((c) => params.append('channel', c));
	filters.categories.forEach((c) => params.append('category', c));
	// Stamp the canonical marker only when there's something to remember, so a
	// pristine page stays at a clean `/inventory/manage` URL.
	if ([...params].length) params.set('f', '1');
	return params;
};

export function ManageItems() {
	const { shop } = useShop();

	const { summary } = useInventoryPageSummary(shop?.id);
	const navigate = useNavigate();
	const goBack = useGoBack('/inventory');
	const [searchParams, setSearchParams] = useSearchParams();

	const [rawQuery, setRawQuery] = useState('');
	const [query, setQuery] = useState('');
	// Seed filters from the URL (Fix-CTA shorthand or a canonical filter URL the
	// page wrote on a previous visit), then mirror every change back to the URL
	// so the filter set survives navigating into a product and back (NEA-5928).
	const [filters, setFilters] = useState(() => parseFilters(searchParams));

	useEffect(() => {
		setSearchParams(serializeFilters(filters), { replace: true });
	}, [filters, setSearchParams]);

	// When exactly one issue is filtered, re-skin the page heading + banner to
	// match the Fix-CTA landing experience.
	const activeIssue = filters.issues.length === 1 ? ISSUE_CONFIG[filters.issues[0]] : null;
	const [page, setPage] = useState(1);
	const [result, setResult] = useState({ items: [], found: 0, loading: true, error: null });
	const [categoryOptions, setCategoryOptions] = useState([]);
	const [catQuery, setCatQuery] = useState('');

	// `channels` isn't a faceted Typesense field, so the channel filter options
	// come from the page-summary's per-channel visibility breakdown instead.
	const channelOptions = useMemo(() => {
		const v = summary?.visibility;
		if (!v) return [];
		const out = [];
		if (v.storefront) out.push('storefront');
		if (v.google) out.push('google');
		for (const c of v.other?.channels ?? []) out.push(c.type);
		return out;
	}, [summary]);
	const [hideOpen, setHideOpen] = useState(false);
	const [unhideOpen, setUnhideOpen] = useState(false);
	const [uploadOpen, setUploadOpen] = useState(false);
	const [hideNoTitleOpen, setHideNoTitleOpen] = useState(false);
	const [actionsOpen, setActionsOpen] = useState(false);
	const [exporting, setExporting] = useState(false);
	const { toast } = useToast();

	// Debounce the search box.
	useEffect(() => {
		const id = setTimeout(() => setQuery(rawQuery.trim()), 300);
		return () => clearTimeout(id);
	}, [rawQuery]);

	useEffect(() => {
		setPage(1);
	}, [query, filters]);

	const filterParts = useMemo(() => {
		const parts = [];
		if (filters.issues.length) {
			parts.push(`issues:=[${filters.issues.map((k) => ISSUE_CONFIG[k].issue).join(',')}]`);
		}
		if (filters.availability === 'in') parts.push('in_stock:=true');
		if (filters.availability === 'out') parts.push('in_stock:=false');
		// The Hidden filter narrows to exactly the rows whose pill renders that
		// value. Active and Inactive are now loose in opposite directions: an
		// Active-with-issues row carries has_errors:=true, so the Inactive
		// filter returns it (alongside Partial) while the Active filter
		// (has_errors:!=true) excludes it — per-channel visibility isn't
		// expressible in Typesense until the `visible_channel_count_i` follow-up
		// (see the computeProductStatus comment above).
		// has_errors is optional in the Typesense schema (`inventory/library/
		// typesense/src/documents.js:41` writes undefined when stock.issues is
		// undefined), and `computeProductStatus` treats undefined as "no errors".
		// Use `!=true` on the "not-inactive" filters so docs where the field is
		// missing match the same way the row tag reads them — otherwise a doc
		// tagged Active in the list wouldn't show under the Active filter.
		if (filters.status === 'active') parts.push('enabled_i:=true && has_errors:!=true');
		if (filters.status === 'inactive') parts.push('has_errors:=true');
		if (filters.status === 'hidden') parts.push('enabled_i:=false && has_errors:!=true');
		if (filters.edited) parts.push('productSource_i:=[myshop,csv]');
		if (filters.channels.length) parts.push(`channels:=[${filters.channels.join(',')}]`);
		if (filters.categories.length) parts.push(`delivery_categories:=[${filters.categories.map(tsValue).join(',')}]`);
		return parts;
	}, [filters]);
	const filterBy = filterParts.join(' && ');

	const runSearch = useCallback(async () => {
		if (!shop?.id) return;
		setResult((r) => ({ ...r, loading: true, error: null }));
		try {
			const collection = await getSearchCollection(shop.organisation, shop.id);
			const data = await collection.search(
				{
					q: query || '*',
					query_by: 'title, barcode, category_titles, delivery_categories',
					filter_by: filterBy,
					facet_by: 'delivery_categories',
					max_facet_values: 100,
					sort_by: 'barcode:asc',
					per_page: PAGE_SIZE,
					page
				},
				{}
			);
			setResult({
				items: data.hits.map(({ document }) => document),
				found: data.found,
				loading: false,
				error: null
			});
			// Seed the category filter options from the first (broadest) search
			// only, so toggling a filter doesn't make options vanish.
			setCategoryOptions((prev) => {
				if (prev.length) return prev;
				const cats = (data.facet_counts ?? []).find((f) => f.field_name === 'delivery_categories');
				return (cats?.counts ?? []).map((c) => c.value);
			});
		} catch (e) {
			setResult({ items: [], found: 0, loading: false, error: e.message ?? 'Search failed.' });
		}
	}, [shop?.id, shop?.organisation, query, filterBy, page]);

	useEffect(() => {
		runSearch();
	}, [runSearch]);

	const clearIssue = () => setFilters((f) => ({ ...f, issues: [] }));

	const setAvailability = (v) => setFilters((f) => ({ ...f, availability: f.availability === v ? null : v }));
	const setStatus = (v) => setFilters((f) => ({ ...f, status: f.status === v ? null : v }));
	const toggleEdited = () => setFilters((f) => ({ ...f, edited: !f.edited }));
	const toggleArrayFilter = (key, value) =>
		setFilters((f) => {
			const has = f[key].includes(value);
			return { ...f, [key]: has ? f[key].filter((v) => v !== value) : [...f[key], value] };
		});

	const activeFilterCount =
		(filters.availability ? 1 : 0) +
		(filters.status ? 1 : 0) +
		(filters.edited ? 1 : 0) +
		filters.issues.length +
		filters.channels.length +
		filters.categories.length;

	const exportCsv = async () => {
		setActionsOpen(false);
		if (!shop?.id || exporting) return;
		setExporting(true);
		toast({ title: 'Preparing your export', description: 'Your CSV will download automatically when ready.' });
		try {
			const client = await myShopClient();
			// The export endpoint takes the same Typesense filter conditions the
			// table search uses, keyed as { '<condition>': true } — so the CSV
			// matches what's on screen (e.g. only products missing images).
			const activeFilters = Object.fromEntries(filterParts.map((part) => [part, true]));
			const { data } = await client.post(`/api/inventory/${shop.id}/export`, { filters: activeFilters });
			if (!data?.url) throw new Error('No download link returned.');
			download(data.filename, data.url, true);
		} catch (e) {
			toast({ title: 'Export failed', description: e?.response?.data?.message ?? e.message, variant: 'destructive' });
		} finally {
			setExporting(false);
		}
	};

	const totalPages = Math.max(1, Math.ceil(result.found / PAGE_SIZE));

	return (
		<div className="space-y-6">
			<header className="space-y-2">
				<button
					type="button"
					onClick={goBack}
					className="inline-flex cursor-pointer items-center gap-1 text-[11px] font-semibold uppercase tracking-[0.14em] text-muted-foreground transition-colors hover:text-foreground"
				>
					<ArrowLeft className="h-3 w-3" /> Back
				</button>
				<div>
					<h1 className="text-3xl font-extrabold tracking-tight md:text-[32px]">
						{activeIssue ? activeIssue.title : 'Manage products'}
					</h1>
					<p className="mt-1.5 text-muted-foreground">
						{activeIssue ? activeIssue.sub : 'Improve product profiles to unlock visibility on more channels.'}
					</p>
				</div>
			</header>

			<div className="flex items-center gap-3">
				<div className="relative flex-1">
					<Search className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
					<input
						type="search"
						placeholder="Search by name, barcode or category"
						value={rawQuery}
						onChange={(e) => setRawQuery(e.target.value)}
						className="h-12 w-full rounded-xl border border-border bg-white pl-11 pr-4 text-sm outline-none transition placeholder:text-muted-foreground focus:border-primary focus:ring-2 focus:ring-primary/20"
					/>
				</div>
				<Popover>
					<PopoverTrigger asChild>
						<button
							type="button"
							className="inline-flex h-12 shrink-0 items-center gap-2 rounded-xl bg-primary px-5 text-sm font-semibold text-primary-foreground transition hover:bg-primary/90"
						>
							Filter
							{activeFilterCount > 0 ? (
								<span className="inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-white px-1 text-xs font-bold text-primary">
									{activeFilterCount}
								</span>
							) : null}
							<ChevronDown className="h-4 w-4" />
						</button>
					</PopoverTrigger>
					<PopoverContent align="end" className="max-h-[70vh] w-[360px] space-y-5 overflow-y-auto rounded-xl p-5">
						<FilterSection title="Availability">
							<CheckPill label="In stock" checked={filters.availability === 'in'} onChange={() => setAvailability('in')} />
							<CheckPill
								label="Out of stock"
								checked={filters.availability === 'out'}
								onChange={() => setAvailability('out')}
							/>
						</FilterSection>
						<FilterSection title="Status">
							<CheckPill label="Active" checked={filters.status === 'active'} onChange={() => setStatus('active')} />
							<CheckPill label="Inactive" checked={filters.status === 'inactive'} onChange={() => setStatus('inactive')} />
							<CheckPill label="Hidden" checked={filters.status === 'hidden'} onChange={() => setStatus('hidden')} />
							<CheckPill label="Edited" checked={filters.edited} onChange={toggleEdited} />
						</FilterSection>
						<FilterSection title="Issues">
							{Object.entries(ISSUE_CONFIG).map(([key, cfg]) => (
								<CheckPill
									key={key}
									label={cfg.label}
									checked={filters.issues.includes(key)}
									onChange={() => toggleArrayFilter('issues', key)}
								/>
							))}
						</FilterSection>
						{channelOptions.length > 0 ? (
							<FilterSection title="Channels">
								{channelOptions.map((c) => (
									<ChipToggle
										key={c}
										label={channelLabel(c)}
										selected={filters.channels.includes(c)}
										onClick={() => toggleArrayFilter('channels', c)}
									/>
								))}
							</FilterSection>
						) : null}
						<div>
							<div className="mb-2 text-sm font-bold text-foreground">Delivery category</div>
							{filters.categories.length > 0 ? (
								<div className="mb-2 flex flex-wrap gap-1.5">
									{filters.categories.map((c) => (
										<button
											key={c}
											type="button"
											onClick={() => toggleArrayFilter('categories', c)}
											className="inline-flex items-center gap-1 rounded-full bg-primary/10 px-2.5 py-1 text-xs font-semibold text-primary"
										>
											{c} <X className="h-3 w-3" />
										</button>
									))}
								</div>
							) : null}
							<input
								type="search"
								value={catQuery}
								onChange={(e) => setCatQuery(e.target.value)}
								placeholder="Search for a category…"
								className="h-11 w-full rounded-xl border border-border bg-white px-4 text-sm outline-none transition placeholder:text-muted-foreground focus:border-primary focus:ring-2 focus:ring-primary/20"
							/>
							{catQuery.trim() ? (
								<div className="mt-2 max-h-44 overflow-y-auto rounded-lg border border-border">
									{categoryOptions
										.filter((c) => c.toLowerCase().includes(catQuery.trim().toLowerCase()))
										.slice(0, 30)
										.map((c) => (
											<button
												key={c}
												type="button"
												onClick={() => {
													toggleArrayFilter('categories', c);
													setCatQuery('');
												}}
												className="block w-full px-3 py-2 text-left text-sm text-foreground transition hover:bg-muted"
											>
												{c}
											</button>
										))}
									{categoryOptions.filter((c) => c.toLowerCase().includes(catQuery.trim().toLowerCase())).length === 0 ? (
										<div className="px-3 py-2 text-sm text-muted-foreground">No matching categories.</div>
									) : null}
								</div>
							) : null}
						</div>
						{activeFilterCount > 0 ? (
							<button
								type="button"
								onClick={() => {
									setFilters(EMPTY_FILTERS);
									setCatQuery('');
								}}
								className="text-xs font-semibold text-primary hover:underline"
							>
								Clear all filters
							</button>
						) : null}
					</PopoverContent>
				</Popover>
				<Popover open={actionsOpen} onOpenChange={setActionsOpen}>
					<PopoverTrigger asChild>
						<button
							type="button"
							className="inline-flex h-12 shrink-0 items-center gap-2 rounded-xl border border-primary bg-white px-5 text-sm font-semibold text-primary transition hover:bg-primary/5"
						>
							Actions <ChevronDown className="h-4 w-4" />
						</button>
					</PopoverTrigger>
					<PopoverContent align="end" className="w-56 rounded-xl p-1.5">
						<ActionItem label={exporting ? 'Preparing export…' : 'Export to CSV'} onClick={exportCsv} disabled={exporting} />
						<ActionItem
							label="Hide products"
							onClick={() => {
								setActionsOpen(false);
								setHideOpen(true);
							}}
						/>
						<ActionItem
							label="Unhide products"
							onClick={() => {
								setActionsOpen(false);
								setUnhideOpen(true);
							}}
						/>
						<ActionItem
							label="Upload product data"
							onClick={() => {
								setActionsOpen(false);
								setUploadOpen(true);
							}}
						/>
						<ActionItem
							label="Hidden without title"
							onClick={() => {
								setActionsOpen(false);
								setHideNoTitleOpen(true);
							}}
						/>
					</PopoverContent>
				</Popover>
			</div>

			<HideProductsDialog open={hideOpen} onOpenChange={setHideOpen} shopId={shop?.id} onDone={runSearch} />
			<UnhideProductsDialog open={unhideOpen} onOpenChange={setUnhideOpen} shopId={shop?.id} onDone={runSearch} />
			<UploadProductDataDialog open={uploadOpen} onOpenChange={setUploadOpen} organisationId={shop?.organisation} shopId={shop?.id} />
			<HideWithoutTitleDialog open={hideNoTitleOpen} onOpenChange={setHideNoTitleOpen} />

			{filters.issues.length > 0 ? (
				<div className="flex items-center justify-between gap-3 rounded-xl bg-warning-soft px-4 py-2.5 text-sm text-warning-soft-foreground">
					<span className="inline-flex items-center gap-2 font-semibold">
						<AlertCircle className="h-4 w-4" />{' '}
						{activeIssue
							? activeIssue.banner
							: `Showing only products with ${filters.issues.map((k) => ISSUE_CONFIG[k].label.toLowerCase()).join(', ')}`}
					</span>
					<button
						type="button"
						onClick={clearIssue}
						className="inline-flex items-center gap-1 text-xs font-semibold hover:underline"
					>
						<X className="h-3.5 w-3.5" /> Clear filter
					</button>
				</div>
			) : null}

			<SoftCard className="overflow-hidden !p-0">
				<div className="overflow-x-auto">
					<table className="w-full text-sm">
						<thead>
							<tr className="border-b border-border/60 bg-muted/30 text-left text-[11px] uppercase tracking-wider text-muted-foreground">
								<th className="w-[40%] px-5 py-2.5 font-semibold">Item</th>
								<th className="px-3 py-2.5 text-right font-semibold">Price</th>
								<th className="px-3 py-2.5 text-right font-semibold">Qty</th>
								<th className="px-3 py-2.5 font-semibold">Category</th>
								<th className="px-3 py-2.5 font-semibold">
									<ColumnHint label="Status" hint={COLUMN_HINTS.status} />
								</th>
								<th className="px-3 py-2.5 font-semibold">
									<ColumnHint label="Issues" hint={COLUMN_HINTS.issues} />
								</th>
								<th className="w-8" />
							</tr>
						</thead>
						<tbody className="divide-y divide-border/60">
							{result.loading ? (
								Array.from({ length: 8 }).map((_, i) => (
									<tr key={i}>
										<td className="px-5 py-3.5" colSpan={7}>
											<Skeleton className="h-5 w-full" />
										</td>
									</tr>
								))
							) : result.error ? (
								<tr>
									<td colSpan={7} className="px-5 py-12 text-center text-sm text-destructive">
										{result.error}
									</td>
								</tr>
							) : result.items.length === 0 ? (
								<tr>
									<td colSpan={7} className="px-5 py-12 text-center text-sm text-muted-foreground">
										No items match your filters.
									</td>
								</tr>
							) : (
								result.items.map((r) => {
									const category = r.category_titles?.[0] ?? r.delivery_categories?.[0] ?? '—';
									// `MissingProductData` is a synthetic roll-up the issues
									// library adds alongside the real gaps — exclude it so the
									// count reflects actual distinct issues.
									const issueCount = Array.isArray(r.issues)
										? r.issues.filter((i) => i !== 'MissingProductData').length
										: 0;
									return (
										<tr
											key={r.barcode}
											className="cursor-pointer transition hover:bg-muted/40"
											onClick={() => navigate(`/inventory/manage/${encodeURIComponent(r.barcode)}`)}
										>
											{/* max-w-0 forces the cell to respect the header's 40% width so the
											    title truncates instead of pushing the action columns off-screen */}
											<td className="max-w-0 px-5 py-3">
												<div className="min-w-0">
													<div className="truncate font-semibold text-foreground">{r.title || r.barcode}</div>
													<div className="text-[11px] tabular-nums text-muted-foreground">{r.barcode}</div>
												</div>
											</td>
											<td className="px-3 py-3 text-right tabular-nums">
												{r.price != null ? `£${Number(r.price).toFixed(2)}` : '—'}
											</td>
											<td className="px-3 py-3 text-right tabular-nums">{r.quantity_i ?? '—'}</td>
											<td className="max-w-[180px] truncate px-3 py-3 text-muted-foreground">{category}</td>
											<td className="px-3 py-3">
												{(() => {
													// An erroring row's verdict needs the shop's enabled channels from the
													// separately-fetched page summary — assert nothing until it resolves,
													// rather than flash Inactive (or stick on it if the summary fetch
													// fails) for a product that is actually live (NEA-6222). Error-free
													// rows derive Active/Hidden from the record alone.
													if (r.has_errors && !summary) {
														return <Skeleton data-testid="status-pending" className="h-5 w-16" />;
													}
													const visibility = computeVisibilityCounts(r, channelOptions);
													const status = computeProductStatus(r, visibility);
													if (status === 'partial') {
														return <VisibilityPill visible={visibility.visible} blocked={visibility.blocked} />;
													}
													return <StatusPill status={status} />;
												})()}
											</td>
											<td className="whitespace-nowrap px-3 py-3">
												{issueCount > 0 ? (
													<span className="inline-flex items-center gap-1.5 whitespace-nowrap text-xs font-semibold text-foreground">
														<span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-destructive/15 text-destructive">
															<AlertCircle className="h-3.5 w-3.5" />
														</span>
														{issueCount} issue{issueCount === 1 ? '' : 's'}
													</span>
												) : (
													<span className="text-xs text-muted-foreground">—</span>
												)}
											</td>
											<td className="px-3 py-3 text-muted-foreground">
												<ChevronRight className="h-4 w-4" />
											</td>
										</tr>
									);
								})
							)}
						</tbody>
					</table>
				</div>

				<div className="flex items-center justify-between border-t border-border/60 px-5 py-3 text-xs text-muted-foreground">
					<span className="tabular-nums">
						{result.found > 0
							? `Showing ${(page - 1) * PAGE_SIZE + 1}–${Math.min(page * PAGE_SIZE, result.found)} of ${result.found.toLocaleString()} items`
							: 'No items'}
					</span>
					<div className="flex items-center gap-1">
						<button
							type="button"
							className="rounded-md px-2.5 py-1 transition hover:bg-accent disabled:opacity-40"
							disabled={page <= 1}
							onClick={() => setPage((p) => Math.max(1, p - 1))}
						>
							Prev
						</button>
						<span className="rounded-md bg-foreground px-2.5 py-1 font-semibold text-background tabular-nums">{page}</span>
						<button
							type="button"
							className="rounded-md px-2.5 py-1 transition hover:bg-accent disabled:opacity-40"
							disabled={page >= totalPages}
							onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
						>
							Next
						</button>
					</div>
				</div>
			</SoftCard>
		</div>
	);
}

function FilterSection({ title, children }) {
	return (
		<div>
			<div className="mb-2 text-sm font-bold text-foreground">{title}</div>
			<div className="flex flex-wrap gap-x-4 gap-y-2">{children}</div>
		</div>
	);
}

function CheckPill({ label, checked, onChange }) {
	return (
		<label className="inline-flex cursor-pointer select-none items-center gap-2">
			<input
				type="checkbox"
				checked={checked}
				onChange={onChange}
				className="h-4 w-4 rounded border-border text-primary focus:ring-primary/30"
			/>
			<span className="text-sm text-foreground">{label}</span>
		</label>
	);
}

function ChipToggle({ label, selected, onClick }) {
	return (
		<button
			type="button"
			onClick={onClick}
			className={cn(
				'inline-flex items-center rounded-lg border px-3 py-2 text-sm transition',
				selected
					? 'border-primary bg-primary/5 font-semibold text-primary'
					: 'border-border bg-white text-foreground hover:bg-accent'
			)}
		>
			{label}
		</button>
	);
}

function ActionItem({ label, onClick, disabled = false }) {
	return (
		<button
			type="button"
			onClick={onClick}
			disabled={disabled}
			className="block w-full rounded-lg px-3 py-2 text-left text-sm text-foreground transition hover:bg-muted disabled:opacity-50"
		>
			{label}
		</button>
	);
}
